import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { EmptyState } from '../components/ui/EmptyState';
import { CtaBand } from '../components/ui/CtaBand';
import { facts, factTopics } from '../data/faqs';
export function Facts() {
  const [topic, setTopic] = useState('All');
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return facts.filter(fact => {
      const matchesTopic = topic === 'All' || fact.topic === topic;
      const matchesQuery = !term || fact.claim.toLowerCase().includes(term) || fact.explanation.toLowerCase().includes(term);
      return matchesTopic && matchesQuery;
    });
  }, [topic, query]);
  const [featured] = facts;
  return <>
      <PageHero eyebrow="Facts & answers" title="Claims, questions and the evidence behind our answers." intro="Misinformation moves faster than campaigns. This page is where every claim about the candidate is answered with a source." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Facts & Answers'
    }]} data-id="element-879" data-mp="32nlvdivlg7ut" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-labelledby="featured-fact-heading" data-id="element-880" data-mp="2imc24we0krk9">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-881" data-mp="2dokdqcckvnkt">
          <h2 id="featured-fact-heading" className="sr-only" data-id="element-882" data-mp="28r0wr4ohwwp6">
            Featured clarification
          </h2>
          <Link to={`/facts/${featured.slug}`} className="group block rounded-media border border-sand-200 bg-white p-7 transition-[border-color,box-shadow] duration-200 ease-emphasis hover:border-green-200 hover:shadow-card lg:p-10" data-id="element-883" data-mp="23yh91ew3gl6n">
            <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-green-700" data-id="element-884" data-mp="1q0115kmhwomx">
              Featured clarification · {featured.topic}
            </span>
            <span className="mt-3 block max-w-3xl font-display text-[26px] leading-snug text-ink sm:text-[32px]" data-id="element-885" data-mp="1l2r71ro5vosr">
              {featured.claim}
            </span>
            <span className="mt-4 inline-block rounded bg-green-50 px-3 py-1.5 text-[15px] font-semibold text-green-800" data-id="element-886" data-mp="1gdgn7n80dwdm">
              {featured.position}
            </span>
            <span className="mt-4 block max-w-3xl text-[16px] leading-relaxed text-slate-600" data-id="element-887" data-mp="16cxxwcxodnob">
              {featured.explanation}
            </span>
            <span className="mt-5 inline-flex items-center gap-2 text-[15px] font-semibold text-green-700" data-id="element-888" data-mp="1u5mq47m5i6v4">
              See the sources
              <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-889" data-mp="1lbrw9vqhc2us" />
            </span>
          </Link>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-20" aria-labelledby="all-facts-heading" data-id="element-890" data-mp="2d0uxt3qzez3q">
        <div className="mx-auto max-w-content px-4 sm:px-6 lg:px-10" data-id="element-891" data-mp="2huft2o16xez8">
          <h2 id="all-facts-heading" className="font-display text-[28px] text-ink" data-id="element-892" data-mp="6cuceq89d69c">
            All clarifications
          </h2>

          <div className="mt-6 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between" data-id="element-893" data-mp="1ej3chpsezs6">
            <div className="relative w-full lg:max-w-sm" data-id="element-894" data-mp="g2xedfxcxujt">
              <label htmlFor="fact-search" className="sr-only" data-id="element-895" data-mp="b4cfbjtudgwy">
                Search facts
              </label>
              <SearchIcon aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-500" data-id="element-896" data-mp="3jl77a493pgw9" />
              <input id="fact-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search a claim or question" className="w-full rounded-lg border border-sand-300 bg-white py-3 pl-11 pr-4 text-[15px] text-ink placeholder:text-slate-500/70 focus:border-green-600 focus:outline-none" data-id="element-897" data-mp="3tjmxm50b0m4p" />
            </div>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by topic" data-id="element-898" data-mp="3egn71faeavu6">
              {factTopics.map(item => {
              const active = topic === item;
              return <button key={item} type="button" aria-pressed={active} onClick={() => setTopic(item)} className={`rounded-lg border px-3.5 py-2 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 text-slate-600 hover:border-slate-500/50 hover:text-ink'}`} data-id="element-899" data-mp="3nh39qmjbdyxz">
                    {item}
                  </button>;
            })}
            </div>
          </div>

          {filtered.length === 0 ? <div className="mt-8" data-id="element-900" data-mp="u4tambj8hsny">
              <EmptyState title="No clarification matches that search" description="Try another term, or send the claim to the campaign and we will publish a response." actionLabel="Clear search" onAction={() => {
            setQuery('');
            setTopic('All');
          }} data-id="element-901" data-mp="u5uoenrmwt9d" />
            </div> : <ul className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-902" data-mp="1cx5b0sqbujvg">
              {filtered.map(fact => <li key={fact.slug} data-id="element-903" data-mp="17y12837beo5n">
                  <Link to={`/facts/${fact.slug}`} className="group flex h-full flex-col rounded-card border border-sand-200 p-6 transition-colors duration-200 ease-emphasis hover:border-green-200 hover:bg-sand-50" data-id="element-904" data-mp="134prvs3anp8z">
                    <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500" data-id="element-905" data-mp="1qutx23w4rx23">
                      {fact.topic} · updated {fact.updated}
                    </span>
                    <span className="mt-2 font-display text-[20px] leading-snug text-ink" data-id="element-906" data-mp="3l50g9d39vnan">
                      {fact.claim}
                    </span>
                    <span className="mt-3 inline-block w-fit rounded bg-green-50 px-2.5 py-1 text-[13px] font-semibold text-green-800" data-id="element-907" data-mp="3bbt9bnsvvz8g">
                      {fact.position}
                    </span>
                    <span className="mt-auto inline-flex items-center gap-2 pt-5 text-[14px] font-semibold text-green-700" data-id="element-908" data-mp="32bdbatuwtkaz">
                      Read the evidence
                      <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-909" data-mp="2se8n6ohi7erj" />
                    </span>
                  </Link>
                </li>)}
            </ul>}
        </div>
      </section>

      <CtaBand title="Seen a claim we have not answered?" description="Send it to the campaign. Published responses carry a source and a date." primary={{
      label: 'Submit a claim',
      to: '/contact'
    }} secondary={{
      label: 'Read the FAQs',
      to: '/faq'
    }} data-id="element-910" data-mp="vkxcngiewsm9" />
    </>;
}